import { supabase } from "@/integrations/supabase/client";
import type { Prompt } from "@/services/promptService";
import { toast } from "sonner";

export const getPopularPrompts = async (limit = 8): Promise<Prompt[]> => {
  try {
    console.log(`Fetching popular prompts with limit: ${limit}`);
    
    const { data, error } = await supabase
      .from('prompts')
      .select(`
        id,
        title,
        description,
        content,
        likes,
        reviews,
        copies,
        created_at,
        user_id,
        categories(id, name, slug)
      `)
      .eq('status', 'approved')
      .order('copies', { ascending: false }) // Primary sort by copies
      .order('reviews', { ascending: false }) // Secondary sort by reviews
      .order('likes', { ascending: false }) // Tertiary sort by likes
      .limit(limit);
      
    if (error) {
      console.error("Supabase error in getPopularPrompts:", error);
      throw error;
    }
    
    console.log(`Got ${data?.length || 0} prompts from database`);
    
    if (!data || data.length === 0) {
      console.log("No prompts returned from database");
      return [];
    }
    
    // Create an array of unique user IDs for fetching authors
    const userIds = [...new Set(data.map(prompt => prompt.user_id).filter(Boolean))];
    console.log(`Found ${userIds.length} unique user IDs`);
    
    // Only query profiles if we have user IDs
    let authorMap = new Map();
    
    if (userIds.length > 0) {
      const { data: authorData, error: authorError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);
      
      if (authorError) {
        console.error("Error fetching author data:", authorError);
        // Continue with empty author map rather than failing completely
      } else if (authorData) {
        authorData.forEach(author => {
          const name = [author.first_name, author.last_name].filter(Boolean).join(' ') || 'Anonymous';
          authorMap.set(author.id, { id: author.id, name });
        });
      }
    }
    
    const formattedPrompts = data.map(prompt => ({
      id: prompt.id,
      title: prompt.title,
      description: prompt.description,
      content: prompt.content,
      category: prompt.categories?.name || 'Uncategorized',
      categorySlug: prompt.categories?.slug || 'uncategorized',
      likes: prompt.likes || 0,
      reviews: prompt.reviews || 0,
      copies: prompt.copies || 0,
      author: authorMap.get(prompt.user_id) || { id: prompt.user_id || 'anonymous', name: 'Anonymous' },
      createdAt: prompt.created_at,
      status: 'approved'
    }));
    
    // Apply time decay factor to create a more nuanced ranking
    const rankedPrompts = formattedPrompts.map(prompt => {
      // Calculate time decay factor (reduces score as prompt ages)
      const createdDate = new Date(prompt.createdAt);
      const now = new Date();
      const ageInDays = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
      const recencyFactor = Math.max(0.5, 1 - (ageInDays / 365)); // Decay over a year to 50%
      
      // Calculate weighted score (same weights as in usePromptSearch)
      const copyWeight = 10; // Highest weight
      const reviewWeight = 5; // Medium-high weight
      const likeWeight = 3; // Medium weight
      const commentWeight = 2; // Medium-low weight (using reviews as proxy for comments)
      
      const score = 
        (prompt.copies * copyWeight) +
        (prompt.reviews * reviewWeight) +
        (prompt.likes * likeWeight) +
        (prompt.reviews * commentWeight) * recencyFactor;
        
      return {
        ...prompt,
        score
      };
    });
    
    // Sort by score descending
    rankedPrompts.sort((a, b) => b.score - a.score);
    
    // Remove the score property before returning
    console.log(`Returning ${rankedPrompts.length} ranked prompts`);
    return rankedPrompts.map(({ score, ...prompt }) => prompt);
  } catch (error) {
    console.error('Error fetching popular prompts:', error);
    throw error; // Re-throw the error to be handled by the calling function
  }
};

export const getLikedPromptsByUserId = async (userId: string): Promise<Prompt[]> => {
  try {
    const { data: likeData, error: likeError } = await supabase
      .from('likes')
      .select('prompt_id')
      .eq('user_id', userId);
      
    if (likeError) throw likeError;
    
    if (!likeData || likeData.length === 0) return [];
    
    const promptIds = likeData.map(like => like.prompt_id);
    
    const { data: promptData, error: promptError } = await supabase
      .from('prompts')
      .select(`
        id,
        title,
        description,
        content,
        likes,
        reviews,
        copies,
        created_at,
        user_id,
        categories(id, name, slug)
      `)
      .in('id', promptIds)
      .order('created_at', { ascending: false });
      
    if (promptError) throw promptError;
    
    if (!promptData || promptData.length === 0) return [];
    
    const userIds = [...new Set(promptData.map(prompt => prompt.user_id))];
    
    const { data: authorData, error: authorError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .in('id', userIds);
    
    if (authorError) throw authorError;
    
    const authorMap = new Map();
    if (authorData) {
      authorData.forEach(author => {
        const name = [author.first_name, author.last_name].filter(Boolean).join(' ') || 'Anonymous';
        authorMap.set(author.id, { id: author.id, name });
      });
    }
    
    return promptData.map(prompt => ({
      id: prompt.id,
      title: prompt.title,
      description: prompt.description,
      content: prompt.content,
      category: prompt.categories?.name || 'Uncategorized',
      categorySlug: prompt.categories?.slug || 'uncategorized',
      likes: prompt.likes || 0,
      reviews: prompt.reviews || 0,
      copies: prompt.copies || 0,
      author: authorMap.get(prompt.user_id) || { id: prompt.user_id, name: 'Anonymous' },
      createdAt: prompt.created_at
    }));
  } catch (error) {
    console.error('Error fetching liked prompts:', error);
    return [];
  }
};

export const getRecentlyUsedPromptsByUserId = async (userId: string): Promise<Prompt[]> => {
  try {
    const { data: copyData, error: copyError } = await supabase
      .from('prompt_copies')
      .select('prompt_id, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
      
    if (copyError) throw copyError;
    
    if (!copyData || copyData.length === 0) return [];
    
    const uniquePromptIds = Array.from(new Set(copyData.map(copy => copy.prompt_id)));
    
    const { data: promptData, error: promptError } = await supabase
      .from('prompts')
      .select(`
        id,
        title,
        description,
        content,
        likes,
        reviews,
        copies,
        created_at,
        user_id,
        categories(id, name, slug)
      `)
      .in('id', uniquePromptIds);
      
    if (promptError) throw promptError;
    
    if (!promptData || promptData.length === 0) return [];
    
    const userIds = [...new Set(promptData.map(prompt => prompt.user_id))];
    
    const { data: authorData, error: authorError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .in('id', userIds);
    
    if (authorError) throw authorError;
    
    const authorMap = new Map();
    if (authorData) {
      authorData.forEach(author => {
        const name = [author.first_name, author.last_name].filter(Boolean).join(' ') || 'Anonymous';
        authorMap.set(author.id, { id: author.id, name });
      });
    }
    
    const promptsMap = new Map();
    promptData.forEach(prompt => {
      promptsMap.set(prompt.id, prompt);
    });
    
    const sortedPrompts = uniquePromptIds
      .map(id => promptsMap.get(id))
      .filter(Boolean);
    
    return sortedPrompts.map(prompt => ({
      id: prompt.id,
      title: prompt.title,
      description: prompt.description,
      content: prompt.content,
      category: prompt.categories?.name || 'Uncategorized',
      categorySlug: prompt.categories?.slug || 'uncategorized',
      likes: prompt.likes || 0,
      reviews: prompt.reviews || 0,
      copies: prompt.copies || 0,
      author: authorMap.get(prompt.user_id) || { id: prompt.user_id, name: 'Anonymous' },
      createdAt: prompt.created_at
    }));
  } catch (error) {
    console.error('Error fetching recently used prompts:', error);
    return [];
  }
};

export const getEnhancedCategories = async () => {
  try {
    const { data: categories, error } = await supabase
      .from('categories')
      .select(`
        id, 
        name, 
        slug, 
        description, 
        icon, 
        parent_id, 
        keywords,
        created_at,
        is_archived
      `)
      .eq('is_archived', false)
      .order('name');
      
    if (error) throw error;
    
    if (!categories) return [];
    
    // Get prompt counts per category with a separate query
    const { data: categoryCounts, error: countError } = await supabase
      .rpc('get_category_counts');
      
    if (countError) {
      console.error('Error fetching category counts:', countError);
      
      // Fallback: Count prompts per category manually
      const { data: promptsData, error: promptsError } = await supabase
        .from('prompts')
        .select('category_id')
        .eq('status', 'approved');
        
      if (promptsError) throw promptsError;
      
      // Create a count map manually
      const countMap = new Map();
      if (promptsData) {
        promptsData.forEach(item => {
          const count = countMap.get(item.category_id) || 0;
          countMap.set(item.category_id, count + 1);
        });
      }
      
      const parentMap = new Map();
      categories.forEach(cat => {
        if (cat.parent_id) {
          if (!parentMap.has(cat.parent_id)) {
            parentMap.set(cat.parent_id, []);
          }
          parentMap.get(cat.parent_id).push(cat);
        }
      });
      
      return categories.map(cat => ({
        ...cat,
        promptCount: countMap.get(cat.id) || 0,
        children: parentMap.get(cat.id) || []
      }));
    }
    
    // If RPC succeeded, use the results
    const countMap = new Map();
    if (categoryCounts) {
      categoryCounts.forEach(item => {
        // Fix: Convert count to number before storing in the map
        countMap.set(item.category_id, parseInt(String(item.count)));
      });
    }
    
    const parentMap = new Map();
    categories.forEach(cat => {
      if (cat.parent_id) {
        if (!parentMap.has(cat.parent_id)) {
          parentMap.set(cat.parent_id, []);
        }
        parentMap.get(cat.parent_id).push(cat);
      }
    });
    
    return categories.map(cat => ({
      ...cat,
      promptCount: countMap.get(cat.id) || 0,
      children: parentMap.get(cat.id) || []
    }));
  } catch (error) {
    console.error('Error fetching enhanced categories:', error);
    return [];
  }
};

export const saveCategory = async (formData: {
  id?: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  parent_id?: string | null;
  keywords?: string[];
}) => {
  try {
    if (formData.id) {
      const { data, error } = await supabase
        .from('categories')
        .update({
          name: formData.name,
          slug: formData.slug,
          description: formData.description || null,
          icon: formData.icon || null,
          parent_id: formData.parent_id || null,
          keywords: formData.keywords || [],
          updated_at: new Date().toISOString()
        })
        .eq('id', formData.id)
        .select('id')
        .single();
        
      if (error) throw error;
      
      return data.id;
    } else {
      const { data, error } = await supabase
        .from('categories')
        .insert({
          name: formData.name,
          slug: formData.slug,
          description: formData.description || null,
          icon: formData.icon || null,
          parent_id: formData.parent_id || null,
          keywords: formData.keywords || []
        })
        .select('id')
        .single();
        
      if (error) throw error;
      
      return data.id;
    }
  } catch (error) {
    console.error('Error saving category:', error);
    throw error;
  }
};

export const archiveCategory = async (categoryId: string) => {
  try {
    const { error } = await supabase
      .from('categories')
      .update({
        is_archived: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', categoryId);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error archiving category:', error);
    throw error;
  }
};

export const mergeCategories = async (sourceId: string, targetId: string) => {
  try {
    const { error: promptError } = await supabase
      .from('prompts')
      .update({ category_id: targetId })
      .eq('category_id', sourceId);
      
    if (promptError) throw promptError;
    
    const { error: archiveError } = await supabase
      .from('categories')
      .update({
        is_archived: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', sourceId);
      
    if (archiveError) throw archiveError;
    
    return true;
  } catch (error) {
    console.error('Error merging categories:', error);
    throw error;
  }
};
